import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import {
  createOrganization,
  createOrganizationLocation,
  getLocationsForOrganization,
  getOrganizations,
  uploadOrganizationImage,
} from "../services/organizationAdminService";
import { Organization, OrganizationLocation } from "../types/organization";
import "../styles/03-components/organizationsAdmin.scss";

type LinkFormItem = {
  type: "website" | "instagram" | "facebook" | "linkedin" | "tiktok";
  url: string;
};

const initialOrganizationForm = {
  id: "",
  name: "",
  logo: "",
  accentColor: "#E53935",
  gradientStart: "#ff3b30",
  gradientEnd: "#c10000",
};

const initialLocationForm = {
  id: "",
  name: "",
  tagline: "",
  description: "",
  distanceKm: 0,
  logo: "",
  links: [{ type: "website", url: "" }] as LinkFormItem[],
};

function slugify(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function OrganizationsAdmin() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [locations, setLocations] = useState<OrganizationLocation[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState("");

  const [organizationForm, setOrganizationForm] = useState(
    initialOrganizationForm
  );
  const [locationForm, setLocationForm] = useState(initialLocationForm);

  const [organizationImageFile, setOrganizationImageFile] =
    useState<File | null>(null);
  const [locationImageFile, setLocationImageFile] = useState<File | null>(null);

  const [organizationImageUploading, setOrganizationImageUploading] =
    useState(false);
  const [locationImageUploading, setLocationImageUploading] = useState(false);

  const [organizationBackgroundImageUrl, setOrganizationBackgroundImageUrl] =
    useState("");
  const [locationBackgroundImageUrl, setLocationBackgroundImageUrl] =
    useState("");

  const [loadingOrganizations, setLoadingOrganizations] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [savingOrganization, setSavingOrganization] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);

  const selectedOrganization = useMemo(
    () => organizations.find((org) => org.id === selectedOrganizationId),
    [organizations, selectedOrganizationId]
  );

  async function loadOrganizations() {
    try {
      setLoadingOrganizations(true);
      const data = await getOrganizations();
      setOrganizations(data);

      if (!selectedOrganizationId && data.length > 0) {
        setSelectedOrganizationId(data[0].id);
      }
    } catch (error) {
      console.error("Fehler beim Laden der Organisationen:", error);
      alert("Organisationen konnten nicht geladen werden.");
    } finally {
      setLoadingOrganizations(false);
    }
  }

  async function loadLocations(organizationId: string) {
    if (!organizationId) {
      setLocations([]);
      return;
    }

    try {
      setLoadingLocations(true);
      const data = await getLocationsForOrganization(organizationId);
      setLocations(data);
    } catch (error) {
      console.error("Fehler beim Laden der Locations:", error);
      alert("Locations konnten nicht geladen werden.");
    } finally {
      setLoadingLocations(false);
    }
  }

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrganizationId) {
      loadLocations(selectedOrganizationId);
    }
  }, [selectedOrganizationId]);

  function handleOrganizationInputChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    setOrganizationForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  function handleLocationInputChange(
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) {
    const { name, value } = e.target;
    setLocationForm((prev) => ({
      ...prev,
      [name]: name === "distanceKm" ? Number(value) : value,
    }));
  }

  function handleOrganizationNameBlur() {
    if (!organizationForm.id.trim() && organizationForm.name.trim()) {
      setOrganizationForm((prev) => ({
        ...prev,
        id: slugify(prev.name),
      }));
    }
  }

  function handleLocationNameBlur() {
    if (!locationForm.id.trim() && locationForm.name.trim()) {
      setLocationForm((prev) => ({
        ...prev,
        id: slugify(prev.name),
      }));
    }
  }

  async function handleUploadOrganizationImage() {
    if (!organizationImageFile) {
      alert("Bitte zuerst ein Bild auswählen.");
      return;
    }

    const organizationId =
      organizationForm.id || slugify(organizationForm.name);

    if (!organizationId) {
      alert("Bitte zuerst Name oder ID der Organisation angeben.");
      return;
    }

    try {
      setOrganizationImageUploading(true);
      const url = await uploadOrganizationImage(
        organizationImageFile,
        `organizations/${organizationId}/cover-${Date.now()}`
      );
      setOrganizationBackgroundImageUrl(url);
      alert("Organisationsbild hochgeladen.");
    } catch (error) {
      console.error(error);
      alert("Bild konnte nicht hochgeladen werden.");
    } finally {
      setOrganizationImageUploading(false);
    }
  }

  async function handleUploadLocationImage() {
    if (!locationImageFile) {
      alert("Bitte zuerst ein Bild auswählen.");
      return;
    }

    const organizationId = selectedOrganizationId || organizationForm.id;
    const locationId = locationForm.id || slugify(locationForm.name);

    if (!organizationId) {
      alert("Bitte zuerst eine Organisation auswählen.");
      return;
    }

    if (!locationId) {
      alert("Bitte zuerst Name oder ID der Location angeben.");
      return;
    }

    try {
      setLocationImageUploading(true);
      const url = await uploadOrganizationImage(
        locationImageFile,
        `organizations/${organizationId}/locations/${locationId}/cover-${Date.now()}`
      );
      setLocationBackgroundImageUrl(url);
      alert("Location-Bild hochgeladen.");
    } catch (error) {
      console.error(error);
      alert("Bild konnte nicht hochgeladen werden.");
    } finally {
      setLocationImageUploading(false);
    }
  }

  async function handleCreateOrganization(e: FormEvent) {
    e.preventDefault();

    const id = organizationForm.id.trim() || slugify(organizationForm.name);

    if (!id || !organizationForm.name.trim()) {
      alert("Bitte mindestens Name und ID der Organisation angeben.");
      return;
    }

    try {
      setSavingOrganization(true);

      await createOrganization({
        id,
        name: organizationForm.name.trim(),
        logo: organizationForm.logo.trim() || undefined,
        backgroundImageUrl: organizationBackgroundImageUrl || undefined,
        accentColor: organizationForm.accentColor,
        gradient: [
          organizationForm.gradientStart,
          organizationForm.gradientEnd,
        ],
      });

      alert("Organisation gespeichert.");

      setOrganizationForm(initialOrganizationForm);
      setOrganizationBackgroundImageUrl("");
      setOrganizationImageFile(null);

      await loadOrganizations();
      setSelectedOrganizationId(id);
    } catch (error) {
      console.error(error);
      alert("Organisation konnte nicht gespeichert werden.");
    } finally {
      setSavingOrganization(false);
    }
  }

  async function handleCreateLocation(e: FormEvent) {
    e.preventDefault();

    if (!selectedOrganizationId) {
      alert("Bitte zuerst eine Organisation auswählen.");
      return;
    }

    const id = locationForm.id.trim() || slugify(locationForm.name);

    if (!id || !locationForm.name.trim()) {
      alert("Bitte mindestens Name und ID der Location angeben.");
      return;
    }

    try {
      setSavingLocation(true);

      await createOrganizationLocation(selectedOrganizationId, {
        id,
        name: locationForm.name.trim(),
        tagline: locationForm.tagline.trim(),
        description: locationForm.description.trim(),
        distanceKm: Number(locationForm.distanceKm) || 0,
        logo: locationForm.logo.trim() || undefined,
        backgroundImageUrl: locationBackgroundImageUrl || undefined,
        links: locationForm.links.filter((item) => item.url.trim() !== ""),
      });

      alert("Location gespeichert.");

      setLocationForm(initialLocationForm);
      setLocationBackgroundImageUrl("");
      setLocationImageFile(null);

      await loadLocations(selectedOrganizationId);
    } catch (error) {
      console.error(error);
      alert("Location konnte nicht gespeichert werden.");
    } finally {
      setSavingLocation(false);
    }
  }

  function updateLink(index: number, field: "type" | "url", value: string) {
    setLocationForm((prev) => {
      const updatedLinks = [...prev.links];
      updatedLinks[index] = {
        ...updatedLinks[index],
        [field]: value,
      } as LinkFormItem;

      return {
        ...prev,
        links: updatedLinks,
      };
    });
  }

  function addLinkField() {
    setLocationForm((prev) => ({
      ...prev,
      links: [...prev.links, { type: "website", url: "" }],
    }));
  }

  function removeLinkField(index: number) {
    setLocationForm((prev) => ({
      ...prev,
      links: prev.links.filter((_, i) => i !== index),
    }));
  }

  return (
    <div className="organizations-admin-page">
      <div className="organizations-admin-page__header">
        <h1>Organizations Admin</h1>
        <p>
          Hier kannst du Organisationen und ihre Standorte direkt in Firebase
          anlegen.
        </p>
      </div>

      <div className="organizations-admin-grid">
        <section className="admin-card">
          <h2>Bestehende Organisationen</h2>

          {loadingOrganizations ? (
            <p>Lade Organisationen...</p>
          ) : organizations.length === 0 ? (
            <p>Noch keine Organisationen vorhanden.</p>
          ) : (
            <div className="organization-list">
              {organizations.map((organization) => (
                <button
                  key={organization.id}
                  type="button"
                  className={
                    selectedOrganizationId === organization.id
                      ? "organization-list__item organization-list__item--active"
                      : "organization-list__item"
                  }
                  onClick={() => setSelectedOrganizationId(organization.id)}
                >
                  <div>
                    <strong>{organization.name}</strong>
                    <span>{organization.id}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="admin-card">
          <h2>Neue Organisation</h2>

          <form onSubmit={handleCreateOrganization} className="admin-form">
            <label>
              Name
              <input
                name="name"
                value={organizationForm.name}
                onChange={handleOrganizationInputChange}
                onBlur={handleOrganizationNameBlur}
                placeholder="z. B. ASB"
              />
            </label>

            <label>
              ID
              <input
                name="id"
                value={organizationForm.id}
                onChange={handleOrganizationInputChange}
                placeholder="z. B. asb"
              />
            </label>

            <label>
              Logo-Key
              <input
                name="logo"
                value={organizationForm.logo}
                onChange={handleOrganizationInputChange}
                placeholder="z. B. asb"
              />
            </label>

            <label>
              Accent Color
              <input
                type="color"
                name="accentColor"
                value={organizationForm.accentColor}
                onChange={handleOrganizationInputChange}
              />
            </label>

            <label>
              Gradient Start
              <input
                type="color"
                name="gradientStart"
                value={organizationForm.gradientStart}
                onChange={handleOrganizationInputChange}
              />
            </label>

            <label>
              Gradient End
              <input
                type="color"
                name="gradientEnd"
                value={organizationForm.gradientEnd}
                onChange={handleOrganizationInputChange}
              />
            </label>

            <div className="upload-box">
              <label>
                Hintergrundbild auswählen
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setOrganizationImageFile(e.target.files?.[0] || null)
                  }
                />
              </label>

              <button
                type="button"
                onClick={handleUploadOrganizationImage}
                disabled={organizationImageUploading}
              >
                {organizationImageUploading
                  ? "Upload läuft..."
                  : "Bild hochladen"}
              </button>

              {organizationBackgroundImageUrl ? (
                <div className="upload-preview">
                  <img
                    src={organizationBackgroundImageUrl}
                    alt="Organization Preview"
                  />
                </div>
              ) : null}
            </div>

            <button type="submit" disabled={savingOrganization}>
              {savingOrganization ? "Speichere..." : "Organisation speichern"}
            </button>
          </form>
        </section>
      </div>

      <div className="organizations-admin-grid organizations-admin-grid--bottom">
        <section className="admin-card">
          <h2>Neue Location</h2>
          <p>
            Ausgewählte Organisation:{" "}
            <strong>
              {selectedOrganization
                ? selectedOrganization.name
                : "Keine ausgewählt"}
            </strong>
          </p>

          <form onSubmit={handleCreateLocation} className="admin-form">
            <label>
              Name
              <input
                name="name"
                value={locationForm.name}
                onChange={handleLocationInputChange}
                onBlur={handleLocationNameBlur}
                placeholder="z. B. ASB Bergisch Land"
              />
            </label>

            <label>
              ID
              <input
                name="id"
                value={locationForm.id}
                onChange={handleLocationInputChange}
                placeholder="z. B. asb-bergisch-land"
              />
            </label>

            <label>
              Tagline
              <input
                name="tagline"
                value={locationForm.tagline}
                onChange={handleLocationInputChange}
                placeholder="z. B. Wir helfen hier und jetzt."
              />
            </label>

            <label>
              Beschreibung
              <textarea
                name="description"
                value={locationForm.description}
                onChange={handleLocationInputChange}
                placeholder="Beschreibung der Location"
                rows={5}
              />
            </label>

            <label>
              Entfernung in km
              <input
                type="number"
                step="0.1"
                name="distanceKm"
                value={locationForm.distanceKm}
                onChange={handleLocationInputChange}
              />
            </label>

            <label>
              Logo-Key
              <input
                name="logo"
                value={locationForm.logo}
                onChange={handleLocationInputChange}
                placeholder="z. B. asb"
              />
            </label>

            <div className="upload-box">
              <label>
                Location-Bild auswählen
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    setLocationImageFile(e.target.files?.[0] || null)
                  }
                />
              </label>

              <button
                type="button"
                onClick={handleUploadLocationImage}
                disabled={locationImageUploading}
              >
                {locationImageUploading ? "Upload läuft..." : "Bild hochladen"}
              </button>

              {locationBackgroundImageUrl ? (
                <div className="upload-preview">
                  <img
                    src={locationBackgroundImageUrl}
                    alt="Location Preview"
                  />
                </div>
              ) : null}
            </div>

            <div className="links-section">
              <div className="links-section__header">
                <h3>Links</h3>
                <button type="button" onClick={addLinkField}>
                  + Link hinzufügen
                </button>
              </div>

              {locationForm.links.map((link, index) => (
                <div key={index} className="link-row">
                  <select
                    value={link.type}
                    onChange={(e) => updateLink(index, "type", e.target.value)}
                  >
                    <option value="website">Website</option>
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="tiktok">TikTok</option>
                    <option value="donation">Sonstiges</option>
                  </select>

                  <input
                    value={link.url}
                    onChange={(e) => updateLink(index, "url", e.target.value)}
                    placeholder="https://..."
                  />

                  <button type="button" onClick={() => removeLinkField(index)}>
                    Entfernen
                  </button>
                </div>
              ))}
            </div>

            <button type="submit" disabled={savingLocation}>
              {savingLocation ? "Speichere..." : "Location speichern"}
            </button>
          </form>
        </section>

        <section className="admin-card">
          <h2>Locations der ausgewählten Organisation</h2>

          {!selectedOrganizationId ? (
            <p>Bitte zuerst eine Organisation auswählen.</p>
          ) : loadingLocations ? (
            <p>Lade Locations...</p>
          ) : locations.length === 0 ? (
            <p>Noch keine Locations vorhanden.</p>
          ) : (
            <div className="location-list">
              {locations.map((location) => (
                <div key={location.id} className="location-list__item">
                  <strong>{location.name}</strong>
                  <span>{location.id}</span>
                  {location.tagline ? <p>{location.tagline}</p> : null}
                  {location.description ? <p>{location.description}</p> : null}
                  <small>{location.distanceKm} km</small>

                  {location.links?.length ? (
                    <div className="location-links">
                      {location.links.map((link, index) => (
                        <a
                          key={`${location.id}-${index}`}
                          href={link.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {link.type}
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
